import type { Database } from '@auctisync/shared';
import { SupabaseClient } from '@supabase/supabase-js';
import type { ConnectionMonitor } from './connection.js';
import { type StoredSession, createRefreshOn401 } from './refresh-on-401.js';
import { registerSessionTools } from './session-tools.js';
import type { StorageAdapter } from './storage.js';

/** The typed Supabase client, bound to the generated AuctiSync schema. */
export type AuctisyncSupabase = SupabaseClient<Database>;

/**
 * Re-exported so consumers depend on this package alone rather than reaching
 * past it to `@supabase/supabase-js`.
 *
 * Not just tidiness: under pnpm's strict linking a consumer that imports
 * supabase-js directly must declare it as its own dependency, which invites two
 * copies of the SDK at different versions — and two copies means two auth
 * clients, each with its own refresh timer, racing over one stored session.
 */
export type { Session, User, AuthError } from '@supabase/supabase-js';

export interface AuctisyncClientConfig {
  /** Supabase project URL. Injected by the host app — the package never reads env. */
  url: string;
  /** Supabase ANON key (never the service-role key). */
  anonKey: string;
  /** Platform auth storage (web localStorage / native SecureStore). */
  storage: StorageAdapter;
  /**
   * Whether to detect an auth session in the page URL. Web handles magic-link /
   * OAuth redirects this way (`true`); native uses a deep link + PKCE, not URL
   * detection (`false`). Defaults to `false` — the safe native default.
   */
  detectSessionInUrl?: boolean;
  /**
   * How long any single HTTP request may hang before it is aborted, in ms.
   * Defaults to {@link DEFAULT_REQUEST_TIMEOUT_MS}. Pass a smaller number in
   * tests; there is no way to disable it, which is the point.
   */
  requestTimeoutMs?: number;
  /**
   * Optional connection monitor. When supplied, EVERY request through this
   * client reports whether it reached the server — which is why connection
   * state is derived at the transport rather than at each call site. A call
   * site that forgets to report is a silent hole; there is nothing to forget
   * here.
   *
   * Realtime rides a WebSocket and does not pass through this fetch, so it is
   * not represented. That is acceptable: the states that matter to a bidder are
   * reached through ordinary requests too.
   */
  connection?: ConnectionMonitor;
  /**
   * Where Edge Functions are called, e.g. `https://example.com/functions/v1`.
   * Defaults to `<url>/functions/v1` — today's behaviour. Its own setting so the
   * functions can move separately from the Data API (move runbook P2).
   */
  functionsUrl?: string;
  /**
   * The Supabase project ref the saved sign-in is stored under (the
   * `<ref>` in `<ref>.supabase.co`). Needed only once `url` is no longer a
   * `*.supabase.co` address — see {@link authStorageKey}.
   */
  projectRef?: string;
}

/**
 * The name the saved sign-in lives under on the device:
 * `sb-<project ref>-auth-token`.
 *
 * 🔴 WHY IT IS PINNED (move runbook P2; Jim, 2026-10-20: nobody is signed out
 * by the move). supabase-js derives this name from the FIRST LABEL OF THE URL'S
 * HOST. Point the client at a new address and, left to itself, it would look
 * for the session under a different name, find nothing, and every person on
 * every device would be signed out by an address change alone. So the name is
 * derived from the project ref, never from whatever address is in use:
 *  - `https://<ref>.supabase.co` (today) gives today's name, unchanged;
 *  - any other address needs `projectRef`, and constructing the client without
 *    it THROWS — a build that would sign everyone out fails where it is made,
 *    not on the customers' devices;
 *  - a local address (`localhost`, `127.0.0.1`, `[::1]`: the Supabase CLI) has
 *    no sessions worth keeping and keeps supabase-js's own name.
 * A `projectRef` that disagrees with a `*.supabase.co` address throws too: that
 * is a staging build pointed at production, or the reverse.
 */
export function authStorageKey(config: Pick<AuctisyncClientConfig, 'url' | 'projectRef'>): string {
  const host = new URL(config.url).hostname.toLowerCase();
  const fromHost = /^([a-z0-9-]+)\.supabase\.co$/.exec(host)?.[1] ?? null;
  const ref = config.projectRef?.trim() || null;
  if (fromHost !== null && ref !== null && ref !== fromHost) {
    throw new Error(
      `projectRef "${ref}" does not match the Supabase address ${host}. ` +
        'One of them belongs to another environment.',
    );
  }
  if (fromHost !== null) return `sb-${fromHost}-auth-token`;
  if (ref !== null) return `sb-${ref}-auth-token`;
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
    // supabase-js's own name for a local address: the first label of the host.
    return `sb-${host.split('.')[0]}-auth-token`;
  }
  throw new Error(
    `The address ${host} is not a *.supabase.co address, so the saved sign-in's name ` +
      'cannot be read from it. Set the project ref (VITE_SUPABASE_PROJECT_REF / ' +
      'EXPO_PUBLIC_SUPABASE_PROJECT_REF) or every signed-in person would be signed out.',
  );
}

/** `functionsUrl`, or today's `<url>/functions/v1`, with no trailing slash. */
export function functionsBaseUrl(
  config: Pick<AuctisyncClientConfig, 'url' | 'functionsUrl'>,
): string {
  const explicit = config.functionsUrl?.trim();
  return (explicit || `${config.url.replace(/\/+$/, '')}/functions/v1`).replace(/\/+$/, '');
}

/**
 * supabase-js has no setting for the functions address: it always builds
 * `<url>/functions/v1`. Its client keeps that in a protected field read each
 * time `.functions` is used, so a subclass can point it elsewhere without
 * touching anything else.
 */
class AuctisyncSupabaseClient extends SupabaseClient<Database> {
  constructor(
    url: string,
    key: string,
    options: ConstructorParameters<typeof SupabaseClient<Database>>[2],
    functionsUrl: string,
  ) {
    super(url, key, options);
    this.functionsUrl = new URL(functionsUrl);
  }
}

/** The saved session, read as supabase-js wrote it. Null for anything that is not one. */
function parseStoredSession(raw: string | null): StoredSession | null {
  if (raw === null) return null;
  try {
    const value = JSON.parse(raw) as Partial<StoredSession> | null;
    return typeof value?.access_token === 'string' && typeof value.refresh_token === 'string'
      ? (value as StoredSession)
      : null;
  } catch {
    return null;
  }
}

/**
 * 20 seconds. Long enough that a slow phone on hotel wifi still completes a
 * cold query, short enough that a person waiting on a screen gets an answer
 * rather than a spinner they have to force-quit.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

/**
 * Wraps `fetch` so no request can hang forever.
 *
 * WHY THIS EXISTS. On 2026-08-10 the bidder app's dashboard sat on "Loading
 * your dashboard…" indefinitely: signed in, real data waiting, and the request
 * simply never came back. Nothing reached Postgres, nothing rejected, nothing
 * was logged — so the screen's `finally` never ran and its error state was
 * unreachable by construction. Every authenticated screen had the same hole,
 * because none of them can show an error for a promise that never settles.
 *
 * A timeout is the only fix that works at the right level. Per-screen guards
 * would each need writing, and each would be one more place to forget.
 */
/** "20s" / "1.5s" — never "0s", which is what `Math.round` gives for anything under 500ms. */
function formatSeconds(ms: number): string {
  const seconds = ms / 1000;
  return `${seconds >= 1 ? Math.round(seconds) : Number(seconds.toFixed(2))}s`;
}

/**
 * Exported so the timeout and connection-reporting behaviour can be tested
 * directly. Going through `createSupabaseClient` drags in postgrest's own
 * retry loop, which turns a millisecond of behaviour into seconds of backoff
 * and obscures which layer is being asserted.
 */
export function withTimeout(timeoutMs: number, monitor?: ConnectionMonitor): typeof fetch {
  return async (input, init) => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    // Compose rather than replace: supabase-js passes its own signal for
    // cancellation (`.abortSignal()`), and dropping it would silently break
    // every caller that relies on it. `AbortSignal.any` is not available on
    // every runtime this package targets, so the composition is manual.
    const caller = init?.signal ?? null;
    const forward = () => {
      controller.abort();
    };
    if (caller !== null) {
      if (caller.aborted) controller.abort();
      else caller.addEventListener('abort', forward);
    }

    try {
      const response = await fetch(input, { ...init, signal: controller.signal });
      // 🔴 REACHED THE SERVER — which is the only claim being made here. A 4xx
      // or 5xx is the server ANSWERING, and answering counts as reachable. A
      // rejected bid must never look like a dropped connection, or the room
      // freezes over a refusal the bidder should simply read.
      monitor?.reportOk();
      return response;
    } catch (cause) {
      // Distinguish OUR timeout from a caller's deliberate cancellation, or
      // the message blames the network for something the app chose to do.
      if (controller.signal.aborted && caller?.aborted !== true) {
        const timedOut = new Error(
          `The server did not respond within ${formatSeconds(timeoutMs)}. Check your connection and try again.`,
        );
        // 🔴 THE NAME IS LOad-BEARING, NOT COSMETIC. postgrest-js retries
        // network failures three times with 1s/2s/4s backoff, and skips that
        // retry loop for one case only: `name === 'AbortError'`. Throwing a
        // plain Error here made a timeout look like an ordinary network blip,
        // so a single `.select()` became four hanging requests plus 7s of
        // backoff — measured. At the 20s default that is roughly 87 seconds
        // before the person staring at the screen learns anything, which is
        // barely better than the hang this replaces.
        timedOut.name = 'AbortError';
        monitor?.reportFailure();
        throw timedOut;
      }
      // A deliberate cancellation is not a connection problem — the app chose
      // it. Reporting it would let ordinary screen navigation escalate toward
      // "offline".
      if (caller?.aborted !== true) monitor?.reportFailure();
      throw cause;
    } finally {
      clearTimeout(timer);
      if (caller !== null) caller.removeEventListener('abort', forward);
    }
  };
}

/**
 * Create the typed Supabase client with an injected storage adapter. The single
 * place client construction happens; the host app supplies url/anonKey/storage
 * so this package stays framework-neutral. The saved sign-in's name is pinned
 * ({@link authStorageKey}) and the functions address is its own setting, so the
 * address can change without signing anyone out.
 *
 * Every request through the client passes two wrappers, outermost first:
 *  1. `createRefreshOn401` — a 401 from the Data API or an Edge Function gets
 *     one session refresh and one retry (precondition P14 of the move). It sits
 *     OUTSIDE the timeout so each attempt, and the refresh, gets its own.
 *  2. `withTimeout` — no request hangs forever, and the connection monitor hears
 *     about every one.
 */
export function createSupabaseClient(config: AuctisyncClientConfig): AuctisyncSupabase {
  // The refresh needs the auth client, which needs the fetch, which is built
  // here — so `client` is named in a closure before it exists. Nothing runs
  // before the assignment below: a request is only ever made through a finished
  // client.
  const storageKey = authStorageKey(config);
  const functionsUrl = functionsBaseUrl(config);
  const refreshOn401 = createRefreshOn401({
    inner: withTimeout(config.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS, config.connection),
    apiUrl: config.url,
    functionsUrl,
    anonKey: config.anonKey,
    auth: () => client.auth,
    readStoredSession: async () => parseStoredSession(await config.storage.getItem(storageKey)),
  });

  const client: AuctisyncSupabase = new AuctisyncSupabaseClient(
    config.url,
    config.anonKey,
    {
      auth: {
        storage: config.storage,
        storageKey,
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: config.detectSessionInUrl ?? false,
      },
      // Covers PostgREST, auth (including the token refresh that hung) and
      // storage. Realtime rides a WebSocket and is unaffected.
      global: { fetch: refreshOn401.fetch },
    },
    functionsUrl,
  );

  // The 401 retry must tell the session's own tokens from a caller's. Every
  // token the session holds arrives here: the stored one, each renewal, a sign-in.
  client.auth.onAuthStateChange((_event, session) => {
    refreshOn401.noteToken(session?.access_token);
  });

  // Requests that go around this client (a native upload, our own services)
  // get the same retry rule and the same functions address: session-request.ts.
  registerSessionTools(client, {
    retryToken: refreshOn401.retryToken,
    functionsUrl,
    anonKey: config.anonKey,
  });

  return client;
}
